import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Avatar,
  Paper,
  Divider,
  IconButton,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  SupportAgent as SupportIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../../consts';
import { useSelector, useDispatch } from 'react-redux';

const getSenderIcon = (senderType) => {
  switch (senderType) {
    case 'admin':
      return <AdminIcon />;
    case 'support':
      return <SupportIcon />;
    default:
      return <PersonIcon />;
  }
};

const getSenderColor = (senderType) => {
  switch (senderType) {
    case 'admin':
      return 'error';
    case 'support':
      return 'warning';
    default:
      return 'primary';
  }
};

const TicketDetail = ({ open, ticketId, onClose, onUpdate, isUserView = false }) => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.token);
  const user = useSelector((state) => state.user);

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (open && ticketId) {
      fetchTicket();
    }
  }, [open, ticketId]);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      // SECURITY: Cookies are sent automatically, no Authorization header needed
      const response = await axios.get(`${API_URL}/tickets/${ticketId}`, {
        withCredentials: true,
      });

      const ticketData = response.data.ticket;
      setTicket(ticketData);
      setStatus(ticketData.status);
      setPriority(ticketData.priority || 'medium');
    } catch (error) {
      console.error('Error fetching ticket:', error);
      if (error.response?.status === 401) {
        dispatch({ type: 'SET_UNAUTHORIZED' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    try {
      setSending(true);
      await axios.post(
        `${API_URL}/tickets/${ticketId}/reply`,
        { message: message.trim() },
        {
          withCredentials: true, // SECURITY: Cookies are sent automatically
        }
      );

      setMessage('');
      fetchTicket(); // Refresh to get new message
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error sending message:', error);
      if (error.response?.status === 401) {
        dispatch({ type: 'SET_UNAUTHORIZED' });
      } else if (error.response?.data?.error) {
        // Show error message to user
        alert(error.response.data.error);
      }
    } finally {
      setSending(false);
    }
  };

  const handleStatusUpdate = async () => {
    try {
      setUpdatingStatus(true);
      await axios.patch(
        `${API_URL}/tickets/${ticketId}`,
        { status, priority },
        {
          withCredentials: true, // SECURITY: Cookies are sent automatically
        }
      );

      fetchTicket();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating status:', error);
      if (error.response?.status === 401) {
        dispatch({ type: 'SET_UNAUTHORIZED' });
      }
    } finally {
      setUpdatingStatus(false);
    }
  };

  const isAdminOrSupport = user?.role === 'admin' || user?.role === 'support';

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!ticket) {
    return null;
  }

  const getTypeLabel = (type) => {
    switch (type) {
      case 'bug':
        return 'Something Not Working';
      case 'user_report':
        return 'Report a User';
      case 'feedback':
        return 'Share Feedback';
      default:
        return 'General Question';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'open':
        return 'Waiting for Response';
      case 'in_progress':
        return "We're Working on It";
      case 'resolved':
        return 'Resolved';
      case 'closed':
        return 'Closed';
      default:
        return status;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              {ticket.subject}
            </Typography>
            <Box display="flex" gap={1} mt={1} flexWrap="wrap">
              <Chip 
                label={getTypeLabel(ticket.type)} 
                size="small" 
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
              <Chip 
                label={getStatusLabel(ticket.status)} 
                size="small" 
                color={ticket.status === 'resolved' ? 'success' : ticket.status === 'in_progress' ? 'warning' : 'primary'}
                sx={{ fontWeight: 500 }}
              />
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ ml: 2 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Created:</strong> {new Date(ticket.create_time).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Last updated:</strong> {new Date(ticket.update_time).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Messages/Conversation */}
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Conversation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isUserView 
            ? "Here's your conversation with our support team. Feel free to add more details or ask questions."
            : "View and respond to messages in this conversation."}
        </Typography>
        <Box sx={{ maxHeight: '400px', overflowY: 'auto', mb: 3 }}>
          {ticket.messages && ticket.messages.length > 0 ? (
            ticket.messages.map((msg, index) => (
              <Paper
                key={index}
                sx={{
                  p: 2.5,
                  mb: 2,
                  bgcolor: msg.sender_type === 'user' 
                    ? 'primary.50' 
                    : msg.sender_type === 'admin' || msg.sender_type === 'support'
                    ? 'success.50'
                    : 'grey.50',
                  borderLeft: `4px solid ${
                    msg.sender_type === 'user' 
                      ? 'primary.main' 
                      : msg.sender_type === 'admin' || msg.sender_type === 'support'
                      ? 'success.main'
                      : 'grey.400'
                  }`,
                }}
              >
                <Box display="flex" gap={2}>
                  <Avatar
                    sx={{
                      bgcolor: `${getSenderColor(msg.sender_type)}.main`,
                      width: 40,
                      height: 40,
                    }}
                  >
                    {getSenderIcon(msg.sender_type)}
                  </Avatar>
                  <Box flex={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {msg.sender_type === 'user' 
                          ? 'You' 
                          : msg.sender_type === 'admin' 
                          ? 'Support Team' 
                          : msg.sender_type === 'support'
                          ? 'Support Team'
                          : 'Support'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(msg.create_time).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                      {msg.message}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No messages yet. Start the conversation below!
              </Typography>
            </Box>
          )}
        </Box>

        {/* Admin/Support Status Update */}
        {isAdminOrSupport && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Update Status
            </Typography>
            <Box display="flex" gap={2} mb={2}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={updatingStatus}
                >
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priority}
                  label="Priority"
                  onChange={(e) => setPriority(e.target.value)}
                  disabled={updatingStatus}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                onClick={handleStatusUpdate}
                disabled={updatingStatus}
                size="small"
              >
                {updatingStatus ? <CircularProgress size={16} /> : 'Update'}
              </Button>
            </Box>
          </>
        )}

        {/* Reply Section */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {isAdminOrSupport ? 'Reply as Support' : 'Add a Reply'}
        </Typography>
        
        {/* Check if ticket is closed/resolved and user is not admin/support */}
        {isUserView && !isAdminOrSupport && (ticket.status === 'closed' || ticket.status === 'resolved') ? (
          <Box
            sx={{
              p: 3,
              mb: 2,
              bgcolor: 'warning.light',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'warning.main',
            }}
          >
            <Typography variant="body1" fontWeight={500} gutterBottom>
              This ticket is {ticket.status === 'closed' ? 'closed' : 'resolved'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You cannot send more messages to this ticket. If you need further assistance, please create a new support request.
            </Typography>
          </Box>
        ) : (
          <>
            {isUserView && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Have more information to share? Ask a question? Just type your message below and we'll get back to you.
              </Typography>
            )}
            <TextField
              fullWidth
              multiline
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={isUserView 
                ? "Type your message here... We're here to help!" 
                : "Type your message..."}
              disabled={sending || (isUserView && !isAdminOrSupport && (ticket.status === 'closed' || ticket.status === 'resolved'))}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={handleSendMessage}
              disabled={!message.trim() || sending || (isUserView && !isAdminOrSupport && (ticket.status === 'closed' || ticket.status === 'resolved'))}
              size="large"
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
                },
                px: 4,
              }}
            >
              {sending ? 'Sending...' : 'Send Message'}
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketDetail;

